import React, { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { constraintConfig } from '../../../constraintConfig';

type OcrFieldRow = {
    fieldKey: string;
    customLabel: React.ReactNode;
    fieldValue: React.ReactNode;
    fieldAlias: React.ReactNode;
};

type Props = {
    show: boolean;
    handleClose: () => void;
    data?: any;
    onViewLineItem?: (expenseItemId: number) => void;
    onViewReport?: (expenseId: number) => void;
};

const OCR_STATUS_NAMES: Record<number, string> = {
    0: 'Processing',
    1: 'Ready To Use',
    2: 'Failed',
    3: 'Attached',
    4: 'Submitted',
    5: 'Already Used',
    6: 'Future Receipt',
    7: 'CC Receipt',
    9: 'All',
};

const RECORD_SOURCE_NAMES: Record<number, string> = {
    [constraintConfig.recordSourceId.NotAvailable]: 'N/A',
    [constraintConfig.recordSourceId.WebApp]: 'Website',
    [constraintConfig.recordSourceId.Mobile]: 'Mobile',
    [constraintConfig.recordSourceId.Integrations]: 'Integrations',
    [constraintConfig.recordSourceId.BankFeed]: 'Bank',
    [constraintConfig.recordSourceId.GorillaExpense]: 'Gorilla Expense',
};

const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value instanceof Date) return value.toLocaleString();
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value, null, 2);
        } catch {
            return String(value);
        }
    }
    return String(value);
};

const copyText = async (text: string, successMsg: string) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.success(successMsg);
    } catch {
        toast.error('Unable to copy');
    }
};

const CopyUrlButton = ({ url }: { url: string }) => {
    const [copied, setCopied] = useState(false);
    if (!url) return null;

    return (
        <button
            type="button"
            className="btn btn-sm btn-light-primary ms-2"
            onClick={async (e) => {
                e.stopPropagation();
                await copyText(url, 'URL copied');
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            }}
        >
            <i className={`fas ${copied ? 'fa-check' : 'fa-copy'} me-1`} />
            {copied ? 'Copied' : 'Copy URL'}
        </button>
    );
};

const getRelationalAlias = (
    fieldKey: string,
    data: any,
    onViewLineItem?: (expenseItemId: number) => void,
    onViewReport?: (expenseId: number) => void
): React.ReactNode => {
    if (fieldKey === 'OcrReadStatus') {
        const id = Number(data?.ocrReadStatus);
        if (Number.isNaN(id) && data?.ocrReadStatus == null) return '—';
        return OCR_STATUS_NAMES[id] || 'Unknown';
    }

    if (fieldKey === 'RecordSourceID') {
        const id = Number(data?.recordSourceID);
        if (Number.isNaN(id) && data?.recordSourceID == null) return '—';
        return RECORD_SOURCE_NAMES[id] || 'Unknown';
    }

    if (fieldKey === 'VendorId') {
        if (data?.vendorId == null || data?.vendorId === '') return '—';
        return data?.vendorName || data?.itemVendorName || 'N/A';
    }

    if (fieldKey === 'UsedExpenseItemId' || fieldKey === 'ExpenseItemId') {
        const id = Number(
            fieldKey === 'UsedExpenseItemId'
                ? data?.usedExpenseItemId || 0
                : data?.expenseItemId || data?.usedExpenseItemId || 0
        );
        if (!id) return '—';
        return (
            <div className="d-flex align-items-center gap-2 flex-wrap">
                <span>Expense Item #{id}</span>
                {onViewLineItem && (
                    <button
                        type="button"
                        className="btn btn-sm btn-light-primary"
                        onClick={(e) => {
                            e.stopPropagation();
                            onViewLineItem(id);
                        }}
                    >
                        <i className="fas fa-list me-1" />
                        View Line Item
                    </button>
                )}
            </div>
        );
    }

    if (fieldKey === 'TrackingId') {
        const trackingId = data?.trackingId;
        if (!trackingId) return '—';
        return (
            <div className="d-flex align-items-center gap-2 flex-wrap">
                <span>{trackingId}</span>
                {data?.expenseId && onViewReport && (
                    <button
                        type="button"
                        className="btn btn-sm btn-light-primary"
                        onClick={(e) => {
                            e.stopPropagation();
                            onViewReport(Number(data.expenseId));
                        }}
                    >
                        <i className="fas fa-file-alt me-1" />
                        View Expense Report
                    </button>
                )}
                <button
                    type="button"
                    className="btn btn-sm btn-light-info"
                    onClick={(e) => {
                        e.stopPropagation();
                        copyText(String(trackingId), 'Tracking ID copied');
                    }}
                >
                    <i className="fas fa-copy me-1" />
                    Copy Tracking ID
                </button>
            </div>
        );
    }

    return '—';
};

const buildFieldValue = (fieldKey: string, data: any, rawValue: any): React.ReactNode => {
    if (
        fieldKey === 'OcrReadStatus' ||
        fieldKey === 'RecordSourceID' ||
        fieldKey === 'VendorId' ||
        fieldKey === 'UsedExpenseItemId' ||
        fieldKey === 'ExpenseItemId'
    ) {
        if (rawValue === null || rawValue === undefined || rawValue === '') return 'N/A';
        return String(rawValue);
    }

    if (fieldKey === 'FileURL') {
        const url = data?.fileURL || '';
        if (!url) return 'N/A';
        return (
            <span className="text-break d-inline-block" style={{ maxWidth: '100%', wordBreak: 'break-all' }}>
                {url}
            </span>
        );
    }

    if (fieldKey === 'OCRTextData') {
        const raw = data?.ocrTextData;
        if (!raw) return 'N/A';
        return (
            <pre
                className="mb-0 p-3 bg-light border rounded fs-8"
                style={{
                    maxHeight: 280,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxWidth: '100%',
                }}
            >
                {String(raw)}
            </pre>
        );
    }

    return formatValue(rawValue);
};

const buildCustomLabel = (fieldKey: string, label: string, data: any): React.ReactNode => {
    if (fieldKey === 'FileURL') {
        return (
            <div className="d-flex align-items-center flex-wrap">
                <span>{label}</span>
                <CopyUrlButton url={data?.fileURL || ''} />
            </div>
        );
    }
    return label;
};

const OCR_FIELD_DEFS: { fieldKey: string; customLabel: string; getValue: (d: any) => any }[] = [
    { fieldKey: 'InvoiceDataDraftId', customLabel: 'SS Receipt ID', getValue: (d) => d.invoiceDataDraftId },
    { fieldKey: 'InvoiceNo', customLabel: 'Receipt Number', getValue: (d) => d.invoiceNo },
    { fieldKey: 'Amount', customLabel: 'Receipt Amount', getValue: (d) => d.amount },
    { fieldKey: 'InvoiceDate', customLabel: 'Receipt Date', getValue: (d) => d.invoiceDate },
    { fieldKey: 'VendorName', customLabel: 'Vendor Name', getValue: (d) => d.vendorName },
    { fieldKey: 'VendorId', customLabel: 'Vendor ID', getValue: (d) => d.vendorId },
    { fieldKey: 'CardNo', customLabel: 'Card Number', getValue: (d) => d.cardNo },
    { fieldKey: 'TaxAmount', customLabel: 'Tax Amount', getValue: (d) => d.taxAmount },
    { fieldKey: 'TipAmount', customLabel: 'Tip Amount', getValue: (d) => d.tipAmount },
    { fieldKey: 'DiscountAmount', customLabel: 'Discount Amount', getValue: (d) => d.discountAmount },
    { fieldKey: 'Alcohol', customLabel: 'Alcohol', getValue: (d) => d.alcohol },
    { fieldKey: 'AttachmentType', customLabel: 'Attachment Type', getValue: (d) => d.attachmentType },
    { fieldKey: 'ReceiptSummary', customLabel: 'Receipt Summary', getValue: (d) => d.receiptSummary },
    { fieldKey: 'ReadDocShortDesc', customLabel: 'Document Short Description', getValue: (d) => d.readDocShortDesc },
    { fieldKey: 'DocForPersons', customLabel: 'Document For Persons', getValue: (d) => d.docForPersons },
    { fieldKey: 'DocDateFrom', customLabel: 'Document Date From', getValue: (d) => d.docDateFrom },
    { fieldKey: 'DocDateTo', customLabel: 'Document Date To', getValue: (d) => d.docDateTo },
    { fieldKey: 'ReceiptDateTimeOffset', customLabel: 'Receipt Date Time Offset', getValue: (d) => d.receiptDateTimeOffset },
    { fieldKey: 'ReceiptDateInUTC', customLabel: 'Receipt Date In UTC', getValue: (d) => d.receiptDateInUTC },
    { fieldKey: 'IsReceiptDateMissed', customLabel: 'Receipt Date Missed', getValue: (d) => d.isReceiptDateMissed },
    { fieldKey: 'Remarks', customLabel: 'Remarks', getValue: (d) => d.remarks },
    { fieldKey: 'OcrReadStatus', customLabel: 'OCR Read Status', getValue: (d) => d.ocrReadStatus },
    { fieldKey: 'RecordSourceID', customLabel: 'Record Source', getValue: (d) => d.recordSourceID },
    { fieldKey: 'UsedExpenseItemId', customLabel: 'Used Expense Item ID', getValue: (d) => d.usedExpenseItemId },
    { fieldKey: 'ExpenseItemId', customLabel: 'Expense Item ID', getValue: (d) => d.expenseItemId },
    { fieldKey: 'TrackingId', customLabel: 'Tracking ID', getValue: (d) => d.trackingId },
    { fieldKey: 'CreatedDT', customLabel: 'Receipt Uploaded Date', getValue: (d) => d.createdDT },
    { fieldKey: 'FileURL', customLabel: 'File URL', getValue: (d) => d.fileURL },
    { fieldKey: 'OCRTextData', customLabel: 'Raw OCR Data', getValue: (d) => d.ocrTextData },
];

const buildRowsFromDraft = (
    data: any,
    onViewLineItem?: (expenseItemId: number) => void,
    onViewReport?: (expenseId: number) => void
): OcrFieldRow[] => {
    if (!data) return [];
    return OCR_FIELD_DEFS.map((def) => ({
        fieldKey: def.fieldKey,
        customLabel: buildCustomLabel(def.fieldKey, def.customLabel, data),
        fieldValue: buildFieldValue(def.fieldKey, data, def.getValue(data)),
        fieldAlias: getRelationalAlias(def.fieldKey, data, onViewLineItem, onViewReport),
    }));
};

const ViewOcrDetailsModal = ({ show, handleClose, data, onViewLineItem, onViewReport }: Props) => {
    const rows = useMemo(
        () => buildRowsFromDraft(data, onViewLineItem, onViewReport),
        [data, onViewLineItem, onViewReport]
    );

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            scrollable
            dialogClassName="modal-ocr-read-details"
        >
            <style>{`
                .modal-ocr-read-details {
                    max-width: 95vw !important;
                    width: 95% !important;
                }
                .modal-ocr-read-details .ocr-field-value-col {
                    max-width: 51%;
                    width: 51%;
                }
            `}</style>
            <Modal.Header closeButton>
                <Modal.Title className="fs-4">OCR Read Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted fs-7 mb-4">
                    Values read into Invoice Data Draft
                    {data?.invoiceDataDraftId ? ` (#${data.invoiceDataDraftId})` : ''}.
                </p>
                <div className="table-responsive ocr-read-details-table-wrap" style={{ maxHeight: '65vh' }}>
                    <table className="table table-row-bordered table-row-gray-300 align-middle gs-0 gy-3 w-100 ocr-read-details-table">
                        <thead className="sticky-top ocr-read-details-thead">
                            <tr className="fw-bold text-muted">
                                <th style={{ width: '15%' }}>Custom Label</th>
                                <th style={{ width: '12%' }}>Field Key</th>
                                <th className="ocr-field-value-col">Field Value</th>
                                <th style={{ width: '22%' }}>Field Alias (Contains Values)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length > 0 ? (
                                rows.map((row) => (
                                    <tr key={row.fieldKey}>
                                        <td className="fw-semibold ocr-read-details-cell">{row.customLabel}</td>
                                        <td className="ocr-read-details-cell">{row.fieldKey}</td>
                                        <td className="ocr-read-details-cell ocr-field-value-col">{row.fieldValue}</td>
                                        <td className="ocr-read-details-cell">{row.fieldAlias}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={4} className="text-center text-muted py-8">
                                        No OCR details available for this receipt.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-light" onClick={handleClose}>
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default ViewOcrDetailsModal;
